import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

/**
 * iter523 — Cold-start overlay (rewrite).
 *
 * Backend can take 5-15s to bind after a K8s pod cold-start. This overlay
 * keeps the screen friendly during that window. Improvements over iter500:
 *  - Probes IMMEDIATELY on mount (no 250ms warm-up delay).
 *  - Each probe has a tight 1.2s timeout (was 3s) so a stuck request
 *    fails fast and retries the next attempt sooner.
 *  - Shows an elapsed-time counter so the user can SEE that work is
 *    happening (not just a static spinner).
 *  - After 8s of failures, surfaces a "Reload page" button as an
 *    escape hatch (covers the rare case where the pod is genuinely
 *    stuck and only a hard reload will fix it).
 *  - Gives up silently after 20 attempts (~24s elapsed) and lets the
 *    app's own axios interceptors handle remaining cold-start latency.
 */
export function ConnectingOverlay() {
  // probing → ready (hidden, fast path)  OR  probing → visible → ready/done
  const [status, setStatus] = useState('probing');
  const [elapsedMs, setElapsedMs] = useState(0);

  const path = (typeof window !== 'undefined' ? window.location.pathname : '') || '';
  // Skip overlay on auth screens — landing should be instant.
  const skip = /^\/(login|register|forgot-password|reset-password|landing)\/?$/.test(path);

  const cancelledRef = useRef(false);
  const startRef = useRef(Date.now());

  useEffect(() => {
    if (skip) return;
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!backend) return;
    cancelledRef.current = false;
    startRef.current = Date.now();

    let attempts = 0;
    const maxAttempts = 20; // ~24s worst case

    // Reveal the overlay only if the first probe doesn't return within 300ms.
    const revealTimer = setTimeout(() => {
      if (!cancelledRef.current && status === 'probing') setStatus('visible');
    }, 300);

    // Tick the elapsed counter every 500ms while visible.
    const tickTimer = setInterval(() => {
      if (cancelledRef.current) return;
      setElapsedMs(Date.now() - startRef.current);
    }, 500);

    const probe = async () => {
      try {
        // iter523 — HEAD /api/health is auth-free + responds in <50ms typically.
        await axios.head(`${backend}/api/health`, { timeout: 1200 });
        if (!cancelledRef.current) {
          clearTimeout(revealTimer);
          setStatus('ready');
          setTimeout(() => { if (!cancelledRef.current) setStatus('done'); }, 250);
        }
      } catch {
        if (cancelledRef.current) return;
        attempts += 1;
        if (attempts >= maxAttempts) {
          setStatus('done');
          return;
        }
        // Retry with a small back-off (500ms first, then up to 1.5s).
        const wait = Math.min(500 + attempts * 100, 1500);
        setTimeout(probe, wait);
      }
    };
    probe();

    return () => {
      cancelledRef.current = true;
      clearTimeout(revealTimer);
      clearInterval(tickTimer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip]);

  if (status === 'probing' || status === 'ready' || status === 'done') return null;

  const seconds = Math.floor(elapsedMs / 1000);
  const stalled = seconds >= 8;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/85 backdrop-blur-sm"
      style={{ animation: 'fadeIn 0.2s ease' }}
      data-testid="connecting-overlay"
    >
      <div className="flex flex-col items-center gap-4 max-w-xs text-center">
        <div className="relative w-14 h-14">
          <div className="absolute inset-0 rounded-full border-2 border-muted" />
          <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-primary animate-spin" />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground font-outfit tracking-tight">Connecting to Smarto…</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {stalled
              ? `Still warming up… ${seconds}s. The server pod takes a moment to wake up after idle.`
              : `Warming up the server${seconds >= 2 ? ` · ${seconds}s` : '…'}`}
          </p>
        </div>
        {/* iter523 — escape hatch shown after 8s if the pod hasn't responded. */}
        {stalled && (
          <button
            type="button"
            onClick={() => { try { window.location.reload(); } catch { /* noop */ } }}
            className="text-[11px] px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:opacity-90 transition-opacity font-semibold"
            data-testid="connecting-overlay-reload"
          >Reload page</button>
        )}
      </div>
      <style>{`@keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }`}</style>
    </div>
  );
}
